// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Torc — the whole game, as a pure state machine over a triangular point lattice. No DOM, no clock,
// no network: the board is an Int8Array, a turn is one integer, and a whole game is the list of those
// integers. That is what makes the lockstep netcode a log replay rather than a synchronisation
// problem, and what lets tests/balance.test.ts play a few hundred games in a second.
//
// THE BOARD IS POINTS, NOT SQUARES. Points sit on a hexagon of radius R with its six VERTICES cut
// off (3R²+3R−5 of them: 31, 55, 85), and three families of straight lines run through them. In
// screen space the q-axis is drawn VERTICAL and the other two fall away at ±60°, which is not a
// cosmetic choice — it makes the board's bounding box almost exactly square (√3·R by 2R−1, i.e. 1.04
// / 0.99 / 0.96 for the three radii) so all three modes share ONE board box and differ only in how
// densely it is filled. A wide board would have forced the biggest mode down to ~35px between
// adjacent points at 375px; upright it is 41px, and the small mode is 68px.
//
// A TURN IS TWO ACTIONS THAT LOOK LIKE ONE. Dropping the marker and riding the ring are a single ply
// (you cannot do one without the other), but CLAIMING a line is its own ply, because it carries a
// real decision — which ring do you give up — and because the OPPONENT may have to claim too, on
// your turn, when your jump flipped their markers into a line. Modelling a claim as a separate ply
// with its own `turn` is what keeps that legible in the log, on the wire, and in the search.

// ── cells ───────────────────────────────────────────────────────────────────────
export const EMPTY = 0;
/** cell values: 1/2 = a marker of side 0/1, 3/4 = a ring of side 0/1. */
export const MARK0 = 1;
export const MARK1 = 2;
export const RING0 = 3;
export const RING1 = 4;

export const markOf = (side: number): number => (side === 0 ? MARK0 : MARK1);
export const ringOf = (side: number): number => (side === 0 ? RING0 : RING1);
export const isMark = (v: number): boolean => v === MARK0 || v === MARK1;
export const isRing = (v: number): boolean => v === RING0 || v === RING1;
/** Which side owns whatever is on this cell, or −1 for empty. */
export const ownerOf = (v: number): number => (v === MARK0 || v === RING0 ? 0 : v === MARK1 || v === RING1 ? 1 : -1);
export const flipped = (v: number): number => (v === MARK0 ? MARK1 : v === MARK1 ? MARK0 : v);

/**
 * Bumped whenever the meaning of a ply changes. Two peers on different revisions are running
 * different games, which is a build mismatch and must be reported as one rather than surfacing as a
 * mysterious desync twenty plies later.
 */
export const RULES_REV = 1;

// ── geometry ────────────────────────────────────────────────────────────────────
/** The six ways off a point. 0,1,2 are the forward halves of the three axes; +3 is the reverse. */
export const DIRS: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [0, 1],
  [1, -1],
  [-1, 0],
  [0, -1],
  [-1, 1],
];
export const AXES = 3;
export const opposite = (dir: number): number => (dir + 3) % 6;

export interface Geom {
  R: number;
  n: number;
  q: Int8Array;
  r: Int8Array;
  /** n×6 — the next point in each direction, or −1 at the rim. */
  nbr: Int32Array;
  /** Every maximal straight line on the board, all three axes. */
  lines: number[][];
  /** Which axis each line belongs to, parallel to `lines`. */
  lineAxis: number[];
  /** n×3 — the line id through this point on each axis. */
  lineOf: Int32Array;
  /** n×3 — this point's position along that line. */
  posOf: Int32Array;
  /**
   * n×n — the direction from a to b if they share an axis, else −1.
   *
   * Worth its 29KB at the biggest radius: without it every applied move re-walks up to six rays to
   * work out which way it went, and the search applies a few hundred thousand of them.
   */
  dirOf: Int8Array;
  /** Normalised 0..1 screen coordinates, y down. */
  px: Float32Array;
  py: Float32Array;
  /** width / height of the board's bounding box, for the CSS aspect-ratio. */
  aspect: number;
  names: string[];
}

const geomCache = new Map<number, Geom>();

/**
 * Build (and cache) the lattice for a radius. Everything downstream — legality, run detection, the
 * renderer, the bot — reads these tables, so this is the only place the board's shape is known.
 */
export function geomFor(R: number): Geom {
  const hit = geomCache.get(R);
  if (hit) return hit;

  const coords: Array<[number, number]> = [];
  const index = new Map<number, number>();
  const key = (q: number, r: number): number => (q + 8) * 64 + (r + 8);
  // The six hexagon VERTICES are cut off — that is what turns a plain hexagon into this board.
  const isVertex = (q: number, r: number): boolean =>
    (Math.abs(q) === R && r === 0) || (Math.abs(r) === R && q === 0) || (Math.abs(q) === R && q === -r);

  for (let q = -R; q <= R; q++) {
    for (let r = -R; r <= R; r++) {
      if (Math.abs(q + r) > R) continue;
      if (isVertex(q, r)) continue;
      index.set(key(q, r), coords.length);
      coords.push([q, r]);
    }
  }

  const n = coords.length;
  const g: Geom = {
    R,
    n,
    q: new Int8Array(n),
    r: new Int8Array(n),
    nbr: new Int32Array(n * 6).fill(-1),
    lines: [],
    lineAxis: [],
    lineOf: new Int32Array(n * AXES).fill(-1),
    posOf: new Int32Array(n * AXES).fill(-1),
    dirOf: new Int8Array(n * n).fill(-1),
    px: new Float32Array(n),
    py: new Float32Array(n),
    aspect: 1,
    names: [],
  };

  coords.forEach(([q, r], i) => {
    g.q[i] = q;
    g.r[i] = r;
  });

  for (let i = 0; i < n; i++) {
    for (let d = 0; d < 6; d++) {
      const [dq, dr] = DIRS[d];
      const j = index.get(key(g.q[i] + dq, g.r[i] + dr));
      g.nbr[i * 6 + d] = j === undefined ? -1 : j;
    }
  }

  for (let i = 0; i < n; i++) {
    for (let d = 0; d < 6; d++) {
      for (let cur = g.nbr[i * 6 + d]; cur >= 0; cur = g.nbr[cur * 6 + d]) g.dirOf[i * n + cur] = d;
    }
  }

  // A maximal line starts at a point with no predecessor along that axis, and runs forward.
  for (let axis = 0; axis < AXES; axis++) {
    for (let i = 0; i < n; i++) {
      if (g.nbr[i * 6 + opposite(axis)] >= 0) continue;
      const line: number[] = [];
      for (let cur = i; cur >= 0; cur = g.nbr[cur * 6 + axis]) line.push(cur);
      if (line.length < 2) continue;
      const id = g.lines.length;
      g.lines.push(line);
      g.lineAxis.push(axis);
      line.forEach((p, pos) => {
        g.lineOf[p * AXES + axis] = id;
        g.posOf[p * AXES + axis] = pos;
      });
    }
  }

  // Screen space: X follows r, Y follows q + r/2 — so the q-axis is a VERTICAL column and the other
  // two fall away at ±60°. See the file header for why upright is the mobile-safe orientation.
  const SQ3 = Math.sqrt(3) / 2;
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  const rawX: number[] = [];
  const rawY: number[] = [];
  for (let i = 0; i < n; i++) {
    const x = SQ3 * g.r[i];
    const y = g.q[i] + g.r[i] / 2;
    rawX.push(x);
    rawY.push(y);
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  const w = maxX - minX;
  const h = maxY - minY;
  g.aspect = w / h;
  for (let i = 0; i < n; i++) {
    g.px[i] = (rawX[i] - minX) / w;
    // y is flipped so that larger q reads as "up the board", which is how the columns are numbered.
    g.py[i] = 1 - (rawY[i] - minY) / h;
  }

  // A readable name per point: the vertical column (axis 1 = fixed q) is the letter, the position
  // up that column is the number. Only ever used in the move list and in aria labels.
  for (let i = 0; i < n; i++) {
    const col = g.q[i] + R;
    const pos = g.posOf[i * AXES + 1];
    g.names.push(`${String.fromCharCode(65 + col)}${pos + 1}`);
  }

  geomCache.set(R, g);
  return g;
}

// ── ply encoding ────────────────────────────────────────────────────────────────
export const PLY_PLACE = 0;
export const PLY_MOVE = 1;
export const PLY_CLAIM = 2;

export const plyKind = (p: number): number => p & 3;
export const plyA = (p: number): number => (p >>> 2) & 511;
export const plyB = (p: number): number => (p >>> 11) & 511;

export const packPlace = (at: number): number => PLY_PLACE | (at << 2);
export const packMove = (from: number, to: number): number => PLY_MOVE | (from << 2) | (to << 11);
/** A claim names WHICH line (anchor point + axis, since two lines can share a lowest point) and
 *  WHICH of your rings pays for it. */
export const packClaim = (anchor: number, axis: number, ring: number): number =>
  PLY_CLAIM | ((anchor * AXES + axis) << 2) | (ring << 11);
export const claimAnchor = (p: number): number => Math.floor(plyA(p) / AXES);
export const claimAxis = (p: number): number => plyA(p) % AXES;
export const claimRing = (p: number): number => plyB(p);

// ── state ───────────────────────────────────────────────────────────────────────
export type Phase = 'place' | 'move' | 'claim';
export type WinReason = 'none' | 'claims' | 'stalled' | 'walkover' | 'nocontest' | 'draw';

export interface SideStats {
  /** Ring moves made (placements are not turns). */
  turns: number;
  rings: number;
  flips: number;
  jumps: number;
  longest: number;
  /** Markers of this colour on the board right now, recomputed on demand. */
  markers: number;
}

export interface State {
  modeId: string;
  R: number;
  g: Geom;
  seed: number;
  /** Markers in a line needed to claim. */
  runLen: number;
  /** Rings each side starts with. */
  rings: number;
  /** Claims needed to win. */
  target: number;
  cell: Int8Array;
  /** Side that must act now — the claimant during a claim, not necessarily the mover. */
  turn: number;
  /** Side whose ring-move turn this is. */
  mover: number;
  phase: Phase;
  ply: number;
  claimed: [number, number];
  placed: [number, number];
  winner: number;
  reason: WinReason;
  log: number[];
  stats: [SideStats, SideStats];
}

export interface PlyEvent {
  kind: 'place' | 'drop' | 'ride' | 'flip' | 'claim' | 'over';
  seat?: number;
  cell?: number;
  from?: number;
  cells?: number[];
  ring?: number;
}

const freshStats = (): SideStats => ({ turns: 0, rings: 0, flips: 0, jumps: 0, longest: 0, markers: 0 });

export interface Shape {
  id: string;
  R: number;
  runLen: number;
  rings: number;
  target: number;
}

export function createState(shape: Shape, seed = 0): State {
  const g = geomFor(shape.R);
  return {
    modeId: shape.id,
    R: shape.R,
    g,
    seed: seed >>> 0,
    runLen: shape.runLen,
    rings: shape.rings,
    target: shape.target,
    cell: new Int8Array(g.n),
    turn: 0,
    mover: 0,
    phase: 'place',
    ply: 0,
    claimed: [0, 0],
    placed: [0, 0],
    winner: -1,
    reason: 'none',
    log: [],
    stats: [freshStats(), freshStats()],
  };
}

export function cloneState(s: State): State {
  return {
    ...s,
    cell: new Int8Array(s.cell),
    claimed: [s.claimed[0], s.claimed[1]],
    placed: [s.placed[0], s.placed[1]],
    log: [],
    stats: [{ ...s.stats[0] }, { ...s.stats[1] }],
  };
}

/** Copy the parts the search mutates, without allocating. */
export function copyInto(dst: State, src: State): void {
  dst.cell.set(src.cell);
  dst.turn = src.turn;
  dst.mover = src.mover;
  dst.phase = src.phase;
  dst.ply = src.ply;
  dst.claimed[0] = src.claimed[0];
  dst.claimed[1] = src.claimed[1];
  dst.placed[0] = src.placed[0];
  dst.placed[1] = src.placed[1];
  dst.winner = src.winner;
  dst.reason = src.reason;
  dst.log.length = 0;
}

/**
 * Is the round finished, for ANY reason?
 *
 * Keyed off `reason`, not off `winner`, and that is the whole point: three of the five endings have
 * no winner at all — a draw, a stopped round, and a round nobody played. An earlier cut asked
 * `winner !== -1 || reason === 'draw'`, which quietly answered "no" to a build mismatch and a
 * desync, so a peer whose round had been declared dead sat on a live board waiting for a ply that
 * was never coming. tests/takeover.test.ts is what found it.
 */
export const isOver = (s: State): boolean => s.winner >= 0 || s.reason !== 'none';

export function ringsOf(s: State, side: number): number[] {
  const want = ringOf(side);
  const out: number[] = [];
  for (let i = 0; i < s.cell.length; i++) if (s.cell[i] === want) out.push(i);
  return out;
}

export function markerCount(s: State, side: number): number {
  const want = markOf(side);
  let n = 0;
  for (let i = 0; i < s.cell.length; i++) if (s.cell[i] === want) n++;
  return n;
}

/** FNV-1a over everything two peers must agree on. */
export function digest(s: State): number {
  let h = 0x811c9dc5;
  const mix = (v: number): void => {
    h ^= v & 0xff;
    h = Math.imul(h, 0x01000193) >>> 0;
  };
  for (let i = 0; i < s.cell.length; i++) mix(s.cell[i]);
  mix(s.turn);
  mix(s.mover);
  mix(s.phase === 'place' ? 0 : s.phase === 'move' ? 1 : 2);
  mix(s.claimed[0]);
  mix(s.claimed[1]);
  mix(s.winner + 2);
  return h >>> 0;
}

export const cellName = (s: State, i: number): string => s.g.names[i] ?? '?';

// ── legality ────────────────────────────────────────────────────────────────────

/**
 * Where the ring on `from` may ride, appended to `out`.
 *
 * The rule in one sentence: run over empty points as far as you like, and you may cross ONE
 * unbroken run of markers — of either colour — but then you must stop on the very first empty point
 * beyond it. A ring blocks in both cases, and so does the rim.
 */
export function rideTargets(s: State, from: number, out: number[]): void {
  const g = s.g;
  for (let d = 0; d < 6; d++) {
    let cur = g.nbr[from * 6 + d];
    // Phase one: open ground.
    while (cur >= 0 && s.cell[cur] === EMPTY) {
      out.push(cur);
      cur = g.nbr[cur * 6 + d];
    }
    if (cur < 0 || isRing(s.cell[cur])) continue;
    // Phase two: we are on the first marker. Cross the whole unbroken run.
    while (cur >= 0 && isMark(s.cell[cur])) cur = g.nbr[cur * 6 + d];
    // Land only if what follows the run is an empty point.
    if (cur >= 0 && s.cell[cur] === EMPTY) out.push(cur);
  }
}

export function rideCount(s: State, from: number): number {
  const out: number[] = [];
  rideTargets(s, from, out);
  return out.length;
}

/** The direction from `from` to `to` if they share an axis, else −1. */
export const dirBetween = (s: State, from: number, to: number): number => s.g.dirOf[from * s.g.n + to];

/**
 * Does this side hold a claimable line? Allocation-free, because `settle()` asks it twice per ply
 * and the search applies hundreds of thousands of plies.
 */
export function hasRun(s: State, side: number): boolean {
  const want = markOf(side);
  const g = s.g;
  for (let li = 0; li < g.lines.length; li++) {
    const line = g.lines[li];
    let run = 0;
    for (let k = 0; k < line.length; k++) {
      run = s.cell[line[k]] === want ? run + 1 : 0;
      if (run >= s.runLen) return true;
    }
  }
  return false;
}

/** Ring positions for a side, written into a caller-owned array. */
export function ringsInto(s: State, side: number, out: number[]): number[] {
  const want = ringOf(side);
  out.length = 0;
  for (let i = 0; i < s.cell.length; i++) if (s.cell[i] === want) out.push(i);
  return out;
}

/** Every maximal run of `side`'s markers that is long enough to claim. */
export function runsFor(s: State, side: number): Array<{ anchor: number; axis: number; cells: number[] }> {
  const want = markOf(side);
  const out: Array<{ anchor: number; axis: number; cells: number[] }> = [];
  const g = s.g;
  for (let li = 0; li < g.lines.length; li++) {
    const line = g.lines[li];
    let run: number[] = [];
    for (let k = 0; k <= line.length; k++) {
      const p = k < line.length ? line[k] : -1;
      if (p >= 0 && s.cell[p] === want) {
        run.push(p);
        continue;
      }
      if (run.length >= s.runLen) out.push({ anchor: Math.min(...run), axis: g.lineAxis[li], cells: run });
      run = [];
    }
  }
  return out;
}

/** The longest unbroken line of `side`'s markers anywhere — used by the evaluation, not by legality. */
export function longestRun(s: State, side: number): number {
  const want = markOf(side);
  const g = s.g;
  let best = 0;
  for (const line of g.lines) {
    let run = 0;
    for (const p of line) {
      run = s.cell[p] === want ? run + 1 : 0;
      if (run > best) best = run;
    }
  }
  return best;
}

/**
 * Every legal ply for whoever must act. The UI, the netcode and the search all read this one list,
 * so an illegal combination is unrepresentable rather than rejected.
 */
export function genPlies(s: State): number[] {
  if (isOver(s)) return [];
  const out: number[] = [];

  if (s.phase === 'place') {
    for (let i = 0; i < s.cell.length; i++) if (s.cell[i] === EMPTY) out.push(packPlace(i));
    return out;
  }

  if (s.phase === 'claim') {
    const runs = runsFor(s, s.turn);
    const rings = ringsOf(s, s.turn);
    for (const run of runs) for (const ring of rings) out.push(packClaim(run.anchor, run.axis, ring));
    return out;
  }

  const dests: number[] = [];
  for (const from of ringsInto(s, s.turn, scratchGen)) {
    dests.length = 0;
    rideTargets(s, from, dests);
    for (const to of dests) out.push(packMove(from, to));
  }
  return out;
}

/** Separate scratch arrays because countPlies() runs INSIDE settle(), inside an applied ply. */
const scratchGen: number[] = [];
const scratchCount: number[] = [];
const scratchRide: number[] = [];

export function countPlies(s: State): number {
  if (isOver(s)) return 0;
  if (s.phase !== 'move') return genPlies(s).length;
  let n = 0;
  for (const from of ringsInto(s, s.turn, scratchCount)) {
    scratchRide.length = 0;
    rideTargets(s, from, scratchRide);
    n += scratchRide.length;
  }
  return n;
}

// ── applying a ply ──────────────────────────────────────────────────────────────

/**
 * Who has to claim next, and what that means for whose turn it is.
 *
 * The MOVER resolves their own lines first, then the opponent resolves theirs — because a jump
 * flips markers, and flipping can hand the other player a line on your turn. Getting this order
 * wrong is invisible until the one game where both sides complete a line on the same ply.
 */
function settle(s: State, events?: PlyEvent[]): void {
  const first = s.mover;
  const second = 1 - s.mover;
  if (hasRun(s, first)) {
    s.phase = 'claim';
    s.turn = first;
    return;
  }
  if (hasRun(s, second)) {
    s.phase = 'claim';
    s.turn = second;
    return;
  }
  s.phase = 'move';
  s.mover = second;
  s.turn = second;
  // A side that cannot ride any ring has run out of game. Whoever has claimed more wins; level is a
  // genuine draw, and saying so is better than inventing a tie-break nobody was playing for.
  if (countPlies(s) === 0) {
    const a = s.claimed[0];
    const b = s.claimed[1];
    if (a === b) {
      s.winner = -1;
      s.reason = 'draw';
    } else {
      s.winner = a > b ? 0 : 1;
      s.reason = 'stalled';
    }
    events?.push({ kind: 'over', seat: s.winner });
  }
}

/**
 * Apply a ply. Returns false if it was not legal — which for the netcode means the two peers are not
 * running the same rules, and is reported as exactly that rather than silently diverging.
 */
export function applyPly(s: State, ply: number, events?: PlyEvent[], record = true): boolean {
  if (isOver(s)) return false;
  const kind = plyKind(ply);
  const g = s.g;

  if (s.phase === 'place') {
    if (kind !== PLY_PLACE) return false;
    const at = plyA(ply);
    if (at >= s.cell.length || s.cell[at] !== EMPTY) return false;
    s.cell[at] = ringOf(s.turn);
    s.placed[s.turn]++;
    events?.push({ kind: 'place', seat: s.turn, cell: at });
    if (record) s.log.push(ply >>> 0);
    s.ply++;
    if (s.placed[0] >= s.rings && s.placed[1] >= s.rings) {
      s.phase = 'move';
      // THE PLAYER WHO PLACED FIRST DOES NOT ALSO MOVE FIRST. Placing first is already an
      // advantage; handing the same side the opening ride as well stacks two of them, and the
      // balance sim measured exactly that. Side 1 opens. See tests/balance.test.ts.
      s.mover = 1;
      s.turn = 1;
    } else {
      s.turn = 1 - s.turn;
    }
    return true;
  }

  if (s.phase === 'claim') {
    if (kind !== PLY_CLAIM) return false;
    const anchor = claimAnchor(ply);
    const axis = claimAxis(ply);
    const ring = claimRing(ply);
    if (anchor >= s.cell.length || axis >= AXES || ring >= s.cell.length) return false;
    if (s.cell[ring] !== ringOf(s.turn)) return false;
    const run = runsFor(s, s.turn).find((x) => x.anchor === anchor && x.axis === axis);
    if (!run) return false;

    for (const p of run.cells) s.cell[p] = EMPTY;
    s.cell[ring] = EMPTY;
    s.claimed[s.turn]++;
    const st = s.stats[s.turn];
    st.rings++;
    if (run.cells.length > st.longest) st.longest = run.cells.length;
    events?.push({ kind: 'claim', seat: s.turn, cells: run.cells.slice(), ring });
    if (record) s.log.push(ply >>> 0);
    s.ply++;

    if (s.claimed[s.turn] >= s.target) {
      s.winner = s.turn;
      s.reason = 'claims';
      events?.push({ kind: 'over', seat: s.winner });
      return true;
    }
    settle(s, events);
    return true;
  }

  // ── the ride ──
  if (kind !== PLY_MOVE) return false;
  const from = plyA(ply);
  const to = plyB(ply);
  if (from >= s.cell.length || to >= s.cell.length) return false;
  if (s.cell[from] !== ringOf(s.turn) || s.cell[to] !== EMPTY) return false;

  const dir = dirBetween(s, from, to);
  if (dir < 0) return false;

  // Re-walk the line rather than trusting the caller: this is the one place a hand-crafted ply off
  // the wire could smuggle in an illegal landing.
  const crossed: number[] = [];
  let cur = g.nbr[from * 6 + dir];
  let seenMarker = false;
  while (cur >= 0 && cur !== to) {
    const v = s.cell[cur];
    if (isRing(v)) return false;
    if (v === EMPTY) {
      // An empty point after the marker run means the ring flew too far.
      if (seenMarker) return false;
    } else {
      seenMarker = true;
      crossed.push(cur);
    }
    cur = g.nbr[cur * 6 + dir];
  }
  if (cur !== to) return false;

  const me = s.turn;
  s.cell[from] = markOf(me);
  s.cell[to] = ringOf(me);
  events?.push({ kind: 'drop', seat: me, cell: from });
  events?.push({ kind: 'ride', seat: me, from, cell: to });
  if (crossed.length) {
    for (const p of crossed) s.cell[p] = flipped(s.cell[p]);
    events?.push({ kind: 'flip', seat: me, cells: crossed.slice() });
    s.stats[me].jumps++;
    s.stats[me].flips += crossed.length;
  }
  s.stats[me].turns++;
  if (record) s.log.push(ply >>> 0);
  s.ply++;
  settle(s, events);
  return true;
}

/** The one entry point the round uses — same as applyPly, but always records. */
export function step(s: State, ply: number, events?: PlyEvent[]): boolean {
  return applyPly(s, ply, events, true);
}

/** Replay a whole log onto a fresh state. Used by the netcode and by the post-game analysis. */
export function replay(shape: Shape, seed: number, log: readonly number[]): State | null {
  const s = createState(shape, seed);
  for (const ply of log) {
    if (isOver(s)) break;
    if (!applyPly(s, ply >>> 0, undefined, true)) return null;
  }
  return s;
}

export function refreshMarkerCounts(s: State): void {
  s.stats[0].markers = markerCount(s, 0);
  s.stats[1].markers = markerCount(s, 1);
}
